import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@material-ui/data-grid";
import { createTheme, withStyles } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/styles";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Fade from "@material-ui/core/Fade";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import Loading from "../../../assets/images/loading.gif";
import { getUser } from "../../../utils/helpers";
import { pendingManualPcr, updateManualPcr } from "../../../actions/pcr";
import BlockedResultsModal from "../../Common/BlockedResultsModal";
import "./ManualPcrPending.css";

const pcrBlockedColumns = [
  { title: "Genista ID", dataIndex: "GenistaID", key: "GenistaID" },
  { title: "Analysis", dataIndex: "Analysis", key: "Analysis" },
  { title: "Reason", dataIndex: "Reason", key: "Reason" },
];

const defaultTheme = createTheme();
function customCheckbox(theme) {
  return {
    "& .MuiCheckbox-root svg": {
      width: 16,
      height: 16,
      backgroundColor: "transparent",
      border: `1px solid ${
        theme.palette.type === "light" ? "#d9d9d9" : "rgb(67, 67, 67)"
      }`,
      borderRadius: 2,
    },
    "& .MuiCheckbox-root svg path": {
      display: "none",
    },
    "& .MuiCheckbox-root.Mui-checked:not(.MuiCheckbox-indeterminate) svg": {
      backgroundColor: "#27AAE1",
      borderColor: "#27AAE1",
    },
    "& .MuiCheckbox-root.Mui-checked .MuiIconButton-label:after": {
      position: "absolute",
      display: "table",
      border: "2px solid #fff",
      borderTop: 0,
      borderLeft: 0,
      transform: "rotate(45deg) translate(-50%,-50%)",
      opacity: 1,
      transition: "all .2s cubic-bezier(.12,.4,.29,1.46) .1s",
      content: '""',
      top: "50%",
      left: "39%",
      width: 5.71428571,
      height: 9.14285714,
    },
    "& .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiIconButton-label:after": {
      width: 8,
      height: 8,
      backgroundColor: "#27AAE1",
      transform: "none",
      top: "39%",
      border: 0,
    },
  };
}

const useStyles = makeStyles((theme) => ({
  filterButtonStyle: {
    fontFamily: "\"Roboto\", sans-serif",
    fontSize: "14px",
    "&:hover": {
      color: "#27AAE1",
      backgroundColor: "transparent",
    },
    color: "#212529",
    marginRight: "2px",
    "& .MuiBadge-colorPrimary": {
      backgroundColor: "#27AAE1",
    },
  },
  quickToolBarStyle: {
    padding: theme.spacing(0.5, 0.5, 0),
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  buttonStyle: {
    marginBottom: "4px",
    color: "#212529",
    borderColor: "#212529",
    "&:hover": {
      color: "#27AAE1",
      borderColor: "#27AAE1",
      backgroundColor: "transparent",
    },
  },
  root: {
    border: 0,
    color:
        theme.palette.type === "light"
          ? "rgba(0,0,0,.85)"
          : "rgba(255,255,255,0.85)",
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    WebkitFontSmoothing: "auto",
    letterSpacing: "normal",
    "& .MuiDataGrid-columnsContainer": {
      backgroundColor: theme.palette.type === "light" ? "#fafafa" : "#1d1d1d",
    },
    "& .MuiDataGrid-iconSeparator": {
      display: "none",
    },
    "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
      borderRight: `1px solid ${
        theme.palette.type === "light" ? "#f0f0f0" : "#303030"
      }`,
    },
    "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
      borderBottom: `1px solid ${
        theme.palette.type === "light" ? "#f0f0f0" : "#303030"
      }`,
    },
    "& .MuiDataGrid-cell": {
      color:
          theme.palette.type === "light"
            ? "rgba(0,0,0,.85)"
            : "rgba(255,255,255,0.65)",
    },
    "& .MuiPaginationItem-root": {
      borderRadius: 0,
    },
    ...customCheckbox(theme),
  },
}),
{ defaultTheme });

const StyledMenu = withStyles({
  paper: {
    border: "1px solid #d3d4d5",
  },
})((props) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "center",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "center",
    }}
    {...props} // eslint-disable-line
  />
));

function QuickActionToolbar({
  anchorEl, handleClick, handleClose, handleMarkNegative, handleMarkPostive,
}) {
  const classes = useStyles();

  return (
    <div className={classes.quickToolBarStyle}>
      <div>
        <GridToolbarFilterButton className={classes.filterButtonStyle} />
        <GridToolbarDensitySelector className={classes.filterButtonStyle} />
      </div>
      <div>
        <Button disableRipple className={classes.buttonStyle} variant="outlined" aria-controls="fade-menu" aria-haspopup="true" onClick={handleClick}>
          Result
        </Button>
        <StyledMenu
          id="customized-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          TransitionComponent={Fade}
        >
          <MenuItem onClick={handleMarkNegative}>Negative</MenuItem>
          <MenuItem onClick={handleMarkPostive}>Positive</MenuItem>
        </StyledMenu>
      </div>
    </div>
  );
}

QuickActionToolbar.propTypes = {
  handleClick: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleMarkNegative: PropTypes.func.isRequired,
  handleMarkPostive: PropTypes.func.isRequired,
  anchorEl: PropTypes.instanceOf(Element),
};

QuickActionToolbar.defaultProps = {
  anchorEl: null,
};

export default function ManualPcrResults() {
  const classes = useStyles();
  const [dataPending, setDataPending] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedResults, setBlockedResults] = useState([]);
  // const [searchText, setSearchText] = useState('');
  // const open = Boolean(anchorEl);

  const columns = [
    {
      field: "GenistaID",
      headerName: "Genista ID",
      width: 180,
      sortable: false,
    },
    {
      field: "Analysis",
      headerName: "Analysis",
      width: 300,
      flex: 1,
      sortable: false,
    },
  ];

  const rows = dataPending ? dataPending.map((data, idx) => (
    {
      id: idx,
      GenistaID: data.GenistaID,
      Analysis: data.Analysis,
    }
  )) : [];

  const fetchMyAPI = async () => {
    setLoading(true);
    try {
      const response = await pendingManualPcr();
      if (response.success) {
        setDataPending(response.List);
      } else {
        toast.error("Something went wrong");
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    fetchMyAPI();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const resultAPI = async (params) => {
    setLoading(true);
    const response = await updateManualPcr(params);
    setLoading(false);

    if (response.success) {
      if (params.result === "Negative") {
        toast.success("Marked all selected as Negative!");
        setSelectionModel([]);
        fetchMyAPI();
      } else if (params.result === "Positive") {
        toast.success("Marked all selected as Positive!");
        setSelectionModel([]);
        fetchMyAPI();
      }
    } else if (response.status207) {
      setBlockedResults(response.result);
      setShowBlockedModal(true);
      fetchMyAPI();
    } else if (params.result === "Negative") {
      toast.error(response.message);
      fetchMyAPI();
    } else if (params.result === "Positive") {
      toast.error(response.message);
      fetchMyAPI();
    }
  };

  const toggleBlockedModal = () => {
    setShowBlockedModal(!showBlockedModal);
    if (!showBlockedModal) {
      setBlockedResults([]);
    }
  };

  const handleMarkNegative = async () => {
    // create the request json object
    const negativeData = [];

    rows.forEach((row, idx) => {
      if (selectionModel.includes(idx)) {
        const objData = {
          Genista_id: row.GenistaID,
          Analysis: row.Analysis,
        };
        negativeData.push(objData);
      }
    });
    if (negativeData.length === 0) {
      toast.warn("Please select at least one analysis!");
      handleClose();
      return;
    }
    if (negativeData && negativeData.length > 0) {
      const user = getUser();
      const params = {
        result: "Negative",
        list: negativeData,
        user_email: user.email,
      };
      resultAPI(params); // mark them negative, API call
    }

    handleClose(); // close the pop-up
  };

  const handleMarkPostive = async () => {
    // create the request json object
    const positiveData = [];

    rows.forEach((row, idx) => {
      if (selectionModel.includes(idx)) {
        const objData = {
          Genista_id: row.GenistaID,
          Analysis: row.Analysis,
        };
        positiveData.push(objData);
      }
    });
    if (positiveData.length === 0) {
      toast.warn("Please select at least one analysis!");
      handleClose();
      return;
    }
    if (positiveData && positiveData.length > 0) {
      const user = getUser();
      const params = {
        result: "Positive",
        list: positiveData,
        user_email: user.email,
      };

      resultAPI(params); // mark them negative, API call
    }

    handleClose(); // close the pop-up
  };

  return (
    <>
      {dataPending
        && (
        <div>
          <DataGrid
            className={classes.root}
            disableSelectionOnClick
            checkboxSelection
            autoHeight
            pagination
            // {...data}
            rows={rows}
            columns={columns}
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
            }}
            selectionModel={selectionModel}
            disableColumnMenu
            components={{ Toolbar: QuickActionToolbar }}
            componentsProps={{
              toolbar: {
                handleClick: (event) => handleClick(event),
                handleClose: () => handleClose(),
                anchorEl,
                handleMarkNegative: () => handleMarkNegative(),
                handleMarkPostive: () => handleMarkPostive(),
              },
            }}
          />
        </div>
        )}
      {loading && (
      <div className="ManualPcrPending__Loading">
        <img src={Loading} alt="" />
      </div>
      )}
      {showBlockedModal && (
        <BlockedResultsModal
          title="Update result failed for the following Samples"
          columns={pcrBlockedColumns}
          blockedResults={blockedResults}
          toggleModal={toggleBlockedModal}
        />
      )}
    </>
  );
}
